import { useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * StepperInput — touch-friendly numeric input for elderly users.
 *
 * Two large square buttons (▲ and ▼) stacked vertically, with the current
 * value and unit displayed to the right. Designed specifically to replace
 * draggable sliders on mobile, which are notoriously tricky for arthritic
 * fingers or PWAs where the touch surface can lag.
 *
 * Each tap increments / decrements by `step`. Press-and-hold auto-repeats
 * (initial 400 ms delay, then every 90 ms) so sweeping through long ranges
 * doesn't require 20 taps.
 */
export const StepperInput = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit = '',
  label = '',
  colorClass = 'cyan', // supported: cyan, emerald, rose, amber, sky, violet
  valueTestId = null,
  incTestId = null,
  decTestId = null,
}) => {
  // Static color map so Tailwind JIT keeps the classes.
  const COLORS = {
    cyan:    { bg: 'bg-cyan-500',    bgHover: 'hover:bg-cyan-600',    bgActive: 'active:bg-cyan-700',    border: 'border-cyan-600' },
    emerald: { bg: 'bg-emerald-500', bgHover: 'hover:bg-emerald-600', bgActive: 'active:bg-emerald-700', border: 'border-emerald-600' },
    rose:    { bg: 'bg-rose-500',    bgHover: 'hover:bg-rose-600',    bgActive: 'active:bg-rose-700',    border: 'border-rose-600' },
    amber:   { bg: 'bg-amber-500',   bgHover: 'hover:bg-amber-600',   bgActive: 'active:bg-amber-700',   border: 'border-amber-600' },
    sky:     { bg: 'bg-sky-500',     bgHover: 'hover:bg-sky-600',     bgActive: 'active:bg-sky-700',     border: 'border-sky-600' },
    violet:  { bg: 'bg-violet-500',  bgHover: 'hover:bg-violet-600',  bgActive: 'active:bg-violet-700',  border: 'border-violet-600' },
  };
  const c = COLORS[colorClass] || COLORS.cyan;

  const canInc = value + step <= max;
  const canDec = value - step >= min;

  // Keep latest value in a ref so the setInterval closure doesn't go stale.
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const inc = () => {
    const v = valueRef.current;
    if (v + step <= max) onChangeRef.current(v + step);
  };
  const dec = () => {
    const v = valueRef.current;
    if (v - step >= min) onChangeRef.current(v - step);
  };

  // Press-and-hold auto-repeat. Refs survive re-renders — using plain `let`
  // variables inside the function body would make setInterval handles orphan
  // on every parent re-render (triggered by onChange), causing stacked
  // intervals that oscillate the value back and forth.
  const holdTimeoutRef = useRef(null);
  const holdIntervalRef = useRef(null);

  const stopHold = () => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  const startHold = (fn) => {
    stopHold(); // clean any lingering handle
    fn(); // immediate first step
    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(fn, 120);
    }, 450);
  };

  const btnBase = `w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center transition-all duration-150 shadow-sm border select-none active:scale-95`;
  const btnActive = `${c.bg} text-white ${c.border} ${c.bgHover} ${c.bgActive}`;
  const btnDisabled = `bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed`;

  return (
    <div className="flex items-center gap-4 sm:gap-5">
      {/* Stacked arrow buttons */}
      <div className="flex flex-col gap-2">
        <button
          type="button"
          aria-label={`Augmenter ${label}`}
          disabled={!canInc}
          onPointerDown={(e) => { e.preventDefault(); if (canInc) startHold(inc); }}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          className={`${btnBase} ${canInc ? btnActive : btnDisabled}`}
          data-testid={incTestId}
          style={{ touchAction: 'manipulation' }}
        >
          <ChevronUp size={28} strokeWidth={3} />
        </button>
        <button
          type="button"
          aria-label={`Diminuer ${label}`}
          disabled={!canDec}
          onPointerDown={(e) => { e.preventDefault(); if (canDec) startHold(dec); }}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          className={`${btnBase} ${canDec ? btnActive : btnDisabled}`}
          data-testid={decTestId}
          style={{ touchAction: 'manipulation' }}
        >
          <ChevronDown size={28} strokeWidth={3} />
        </button>
      </div>

      {/* Value display */}
      <div className="flex flex-col leading-tight">
        <span
          className="text-slate-900 font-bold text-4xl sm:text-5xl tabular-nums"
          style={{ fontFamily: 'system-ui' }}
          data-testid={valueTestId}
        >
          {value}
        </span>
        {unit && (
          <span className="text-slate-500 text-sm sm:text-base font-medium mt-1">{unit}</span>
        )}
      </div>
    </div>
  );
};

export default StepperInput;
